import { useState } from 'react';
import ReactDOM from 'react-dom';
import type { Filter, FilterDataSearchDynamic } from '@/app/common/filters/core/filtersTypes.ts';
import type { SuggestionFetcher } from '@/app/components/search/components/AutocompleteBasic';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { IconError } from '@/app/common/icons/libraryIcons';
import {
  FilterSearchDynamicDelete,
  FilterSearchDynamicMobile,
  FilterSearchDynamicMobileButton,
  FilterSearchDynamicStyled,
} from './styled.ts';
import { type FilterSearchDynamicSelected, FilterSearchDynamicSelector } from './FilterSearchDynamicSelector.tsx';
import { FilterSearchDynamicModal } from './FilterSearchDynamicModal.tsx';
import MagnifyingGlass from './MagnifyingGlass.tsx';

type Props = {
  filter: Filter;
  name: string;
  suggestionFetcher: SuggestionFetcher;
  onUpdate: (e: FilterSearchDynamicSelected) => void;
};

export const FilterSearchDynamicComponent = ({ filter, name, suggestionFetcher, onUpdate }: Props) => {
  const filterData = filter.data as FilterDataSearchDynamic;
  const { value, placeholder } = filterData;

  const { isMobile, useEvoFilters } = useFiltersUiData();
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState<boolean>(false);
  const valueToShow = value || placeholder;
  const showDeleteButton = !!value;

  const handleFilterSearchDynamicClicked = () => {
    setShowModal(true);
  };

  const handleDeleteClicked = () => {
    onUpdate({ target: { name, value: '' } });
  };

  return (
    <FilterSearchDynamicStyled>
      {isMobile ? (
        <FilterSearchDynamicMobile $evo={useEvoFilters} onClick={handleFilterSearchDynamicClicked}>
          {(useEvoFilters || !value) && <MagnifyingGlass size={useEvoFilters ? 20 : 21} />}
          <FilterSearchDynamicMobileButton $evo={useEvoFilters} showDeleteButton={showDeleteButton}>
            {valueToShow}
          </FilterSearchDynamicMobileButton>
          {showDeleteButton && (
            <FilterSearchDynamicDelete
              type="button"
              $evo={useEvoFilters}
              onClick={handleDeleteClicked}
              aria-label={t('vacation.oss.filters.clear')}
            >
              <IconError size={useEvoFilters ? 16 : 18} />
            </FilterSearchDynamicDelete>
          )}
        </FilterSearchDynamicMobile>
      ) : (
        <FilterSearchDynamicSelector
          filter={filter}
          name={name}
          suggestionFetcher={suggestionFetcher}
          onUpdate={onUpdate}
        />
      )}
      {showModal &&
        ReactDOM.createPortal(
          <FilterSearchDynamicModal
            filter={filter}
            name={name}
            suggestionFetcher={suggestionFetcher}
            onUpdate={onUpdate}
            setShowModal={setShowModal}
          />,
          document.querySelector('[data-openx-modal-paper]') || document.body
        )}
    </FilterSearchDynamicStyled>
  );
};
